// نظام الأدوار الجديد المبسط

// الأقسام المتاحة
export type Department =
  | 'IT' // إدارة تكنولوجيا المعلومات
  | 'Accounting' // إدارة الحسابات
  | 'HR' // إدارة الموارد البشرية
  | 'CustomerService' // إدارة خدمة العملاء
  | 'Education' // إدارة التعليم
  | 'StudentAffairs'; // إدارة شؤون الطلبة

// المسميات الوظيفية الأساسية
export type BaseJobTitle =
  | 'Admin' // إداري (أعلى صلاحية)
  | 'Supervisor' // مشرف
  | 'Senior' // موظف أول
  | 'Junior'; // موظف مبتدئ

// المسميات الوظيفية (تشمل الأساسية والمخصصة)
export type JobTitle = BaseJobTitle | string;

// أسماء الأقسام بالعربية
export const DEPARTMENT_NAMES: Record<Department, string> = {
  IT: 'إدارة تكنولوجيا المعلومات',
  Accounting: 'إدارة الحسابات',
  HR: 'إدارة الموارد البشرية',
  CustomerService: 'إدارة خدمة العملاء',
  Education: 'إدارة التعليم',
  StudentAffairs: 'إدارة شؤون الطلبة',
};

// أسماء المسميات الوظيفية الأساسية بالعربية
export const JOB_TITLE_NAMES: Record<BaseJobTitle, string> = {
  Admin: 'إداري',
  Supervisor: 'مشرف',
  Senior: 'موظف أول',
  Junior: 'موظف مبتدئ',
};

// دالة للحصول على الاسم المعروض للمسمى الوظيفي
export function getJobTitleDisplayName(jobTitle: JobTitle): string {
  return JOB_TITLE_NAMES[jobTitle as BaseJobTitle] || jobTitle;
}

// مستويات الصلاحيات حسب المسمى الوظيفي الأساسي
export const PERMISSION_LEVELS: Record<BaseJobTitle, number> = {
  Admin: 4, // أعلى صلاحية
  Supervisor: 3, // صلاحية إشرافية
  Senior: 2, // صلاحية متوسطة
  Junior: 1, // صلاحية أساسية
};

// الصلاحيات المتاحة
export interface EmployeePermissions {
  // صلاحيات النظام العامة
  canViewDashboard: boolean;
  canViewReports: boolean;
  canManageOwnProfile: boolean;

  // صلاحيات إدارة البيانات
  canViewUsers: boolean;
  canCreateUsers: boolean;
  canEditUsers: boolean;
  canDeleteUsers: boolean;

  // صلاحيات إدارة الموظفين
  canViewEmployees: boolean;
  canCreateEmployees: boolean;
  canEditEmployees: boolean;
  canDeleteEmployees: boolean;

  // صلاحيات إدارة الطلاب (لإدارة التعليم وشؤون الطلبة)
  canViewStudents: boolean;
  canCreateStudents: boolean;
  canEditStudents: boolean;
  canDeleteStudents: boolean;

  // صلاحيات المالية (لإدارة الحسابات)
  canViewPayments: boolean;
  canCreatePayments: boolean;
  canEditPayments: boolean;
  canDeletePayments: boolean;

  // صلاحيات النظام المتقدمة (للإداريين والمشرفين)
  canManageRoles: boolean;
  canManageSystem: boolean;
  canViewSystemLogs: boolean;
}

// دالة لتحديد الصلاحيات بناءً على القسم والمسمى الوظيفي
export function getEmployeePermissions(
  department: Department,
  jobTitle: JobTitle
): EmployeePermissions {
  // للمسميات المخصصة، استخدم مستوى Junior كافتراضي
  const level =
    PERMISSION_LEVELS[jobTitle as BaseJobTitle] || PERMISSION_LEVELS.Junior;
  const isAdmin = jobTitle === 'Admin';
  const isSupervisor = jobTitle === 'Supervisor' || isAdmin;
  const isSenior = jobTitle === 'Senior' || isSupervisor;

  // الصلاحيات الأساسية لجميع الموظفين
  const basePermissions: EmployeePermissions = {
    canViewDashboard: true,
    canViewReports: level >= 2,
    canManageOwnProfile: true,

    canViewUsers: level >= 2,
    canCreateUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,

    canViewEmployees: level >= 2,
    canCreateEmployees: false,
    canEditEmployees: false,
    canDeleteEmployees: false,

    canViewStudents: false,
    canCreateStudents: false,
    canEditStudents: false,
    canDeleteStudents: false,

    canViewPayments: false,
    canCreatePayments: false,
    canEditPayments: false,
    canDeletePayments: false,

    canManageRoles: false,
    canManageSystem: false,
    canViewSystemLogs: false,
  };

  // تخصيص الصلاحيات حسب القسم
  switch (department) {
    case 'IT':
      return {
        ...basePermissions,
        canViewUsers: true,
        canCreateUsers: isSupervisor,
        canEditUsers: isSupervisor,
        canDeleteUsers: isAdmin,
        canManageRoles: isAdmin,
        canManageSystem: isSupervisor,
        canViewSystemLogs: isSenior,
      };

    case 'HR':
      return {
        ...basePermissions,
        canViewEmployees: true,
        canCreateEmployees: isSenior,
        canEditEmployees: isSenior,
        canDeleteEmployees: isSupervisor,
        canViewUsers: true,
        canCreateUsers: isSupervisor,
        canEditUsers: isSenior,
      };

    case 'Accounting':
      return {
        ...basePermissions,
        canViewPayments: true,
        canCreatePayments: isSenior,
        canEditPayments: isSenior,
        canDeletePayments: isSupervisor,
        canViewUsers: isSenior,
        canViewStudents: true,
      };

    case 'Education':
      return {
        ...basePermissions,
        canViewStudents: true,
        canCreateStudents: isSenior,
        canEditStudents: isSenior,
        canDeleteStudents: isSupervisor,
        canViewUsers: isSenior,
      };

    case 'StudentAffairs':
      return {
        ...basePermissions,
        canViewStudents: true,
        canCreateStudents: isSenior,
        canEditStudents: isSenior,
        canDeleteStudents: isSupervisor,
        canViewPayments: isSenior,
        canViewUsers: isSenior,
      };

    case 'CustomerService':
      return {
        ...basePermissions,
        canViewUsers: true,
        canEditUsers: level >= 2,
        canViewStudents: true,
        canEditStudents: isSenior,
        canViewPayments: isSenior,
      };

    default:
      return basePermissions;
  }
}

// نوع الموظف الجديد
export interface NewEmployee {
  id?: string;
  uid?: string;

  // البيانات الأساسية
  name: string;
  email: string;
  phone: string;
  employeeId: string;

  // معلومات العمل
  department: Department;
  jobTitle: JobTitle;
  position: string; // المسمى الوظيفي بالعربية (اختياري)

  // الصلاحيات
  permissions: EmployeePermissions;

  // الراتب
  salary: {
    base: number;
    currency: 'EGP';
    payrollType: 'monthly' | 'daily' | 'hourly';
    allowances: {
      transport: number;
      housing: number;
      food: number;
      other: number;
    };
  };

  // المعلومات الشخصية
  personalInfo: {
    dateOfBirth: Date;
    nationalId: string;
    address: string;
    emergencyContact: {
      name: string;
      phone: string;
      relation: string;
    };
  };

  // معلومات التوظيف
  employment: {
    hireDate: Date;
    contractType: 'full_time' | 'part_time' | 'contract';
    contractEndDate?: Date;
  };

  // الحالة
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}
